/**
 * Syncs @nuxtjs/i18n locale from the [locale] route param.
 *
 * Problem: @nuxtjs/i18n with strategy:'no_prefix' always defaults to 'en' —
 * it doesn't inspect the custom [locale] URL segment. This affects both SSR
 * (wrong server HTML) and client hydration (route-locale-detect resets to 'en').
 *
 * Fix:
 * 1. On plugin setup — set locale immediately (SSR + initial client render).
 * 2. On router.afterEach — re-apply locale after every navigation so
 *    route-locale-detect (which fires on navigation) can't override it.
 *
 * en/uk/ru are bundled in i18n.config.ts — no fetch needed.
 * Other locales are loaded from filesystem (server) or /i18n/*.json (client).
 */

import { SUPPORTED_LOCALES } from '@/domain/services/i18n.service'

function hasMessages(i18n: any, locale: string): boolean {
  const msgs = i18n.getLocaleMessage?.(locale) ?? {}
  return Object.keys(msgs).length > 0
}

async function applyLocale(nuxtApp: ReturnType<typeof useNuxtApp>, locale: string) {
  const i18n = nuxtApp.$i18n as any

  if (!hasMessages(i18n, locale)) {
    if (import.meta.server) {
      const { readFile } = await import('node:fs/promises')
      const { resolve } = await import('node:path')
      const dir =
        process.env.NODE_ENV === 'production'
          ? resolve(process.cwd(), '.output', 'public', 'i18n')
          : resolve(process.cwd(), 'public', 'i18n')
      try {
        const raw = await readFile(resolve(dir, `${locale}.json`), 'utf-8')
        i18n.setLocaleMessage(locale, JSON.parse(raw))
      } catch {
        // Locale file not yet translated — fall back to default (en)
      }
    } else {
      try {
        const messages = await $fetch<Record<string, unknown>>(`/i18n/${locale}.json`)
        i18n.setLocaleMessage(locale, messages)
      } catch {
        // Network error — fall back to default (en)
      }
    }
  }

  i18n.locale.value = locale
}

export default defineNuxtPlugin({
  name: 'i18n-route-locale',
  dependsOn: ['i18n:plugin'],
  async setup(nuxtApp) {
    // 1. Apply on initial load (covers SSR and first client render)
    const route = useRoute()
    const locale = route.params.locale as string | undefined
    if (locale && (SUPPORTED_LOCALES as readonly string[]).includes(locale)) {
      await applyLocale(nuxtApp, locale)
    }

    // 2. Re-apply after every navigation (prevents route-locale-detect from
    //    resetting to 'en' on client-side navigation with no_prefix strategy)
    if (import.meta.client) {
      nuxtApp.$router.afterEach((to) => {
        const toLocale = to.params.locale as string | undefined
        if (toLocale && (SUPPORTED_LOCALES as readonly string[]).includes(toLocale)) {
          applyLocale(nuxtApp, toLocale)
        }
      })
    }
  },
})
